import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Header from './header';
import SideBarMenu from './sideBarMenu';
import LoaderSpinner from '../loaderSpinner';
import {
  getAppointmentNotificationEmails,
  setAppointmentNotificationEmails,
  getAISummaryTemplate,
  setAISummaryTemplate,
  DEFAULT_AI_SUMMARY_TEMPLATE,
} from '../../services/firestore';

function AdminSettings() {
  const [activeTab, setActiveTab] = useState(0);

  // Tab 1 — Notification Emails
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Tab 2 — Summary Template
  const [template, setTemplate] = useState('');
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  const adminData = JSON.parse(localStorage.getItem('UserProfileAdmin') || '{}');
  const adminId = localStorage.getItem('UserIdAdmin');

  useEffect(() => {
    loadEmails();
    loadTemplate();
  }, []);

  const loadEmails = async () => {
    setLoading(true);
    const list = await getAppointmentNotificationEmails();
    setEmails(list);
    setLoading(false);
  };

  const loadTemplate = async () => {
    setLoadingTemplate(true);
    const saved = await getAISummaryTemplate();
    setTemplate(saved || DEFAULT_AI_SUMMARY_TEMPLATE);
    setLoadingTemplate(false);
  };

  const handleAddEmail = () => {
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error('Invalid email address', { position: toast.POSITION.TOP_RIGHT });
      return;
    }
    if (emails.includes(trimmed)) {
      toast.error('Email already added', { position: toast.POSITION.TOP_RIGHT });
      return;
    }
    setEmails([...emails, trimmed]);
    setNewEmail('');
  };

  const handleRemoveEmail = (email) => {
    setEmails(emails.filter((e) => e !== email));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await setAppointmentNotificationEmails(emails, {
        adminId,
        adminEmail: adminData.email || '',
        adminName: adminData.fullName || '',
      });
      toast.success('Notification emails saved successfully!', { position: toast.POSITION.TOP_RIGHT });
    } catch (e) {
      toast.error('Failed to save. Please try again.', { position: toast.POSITION.TOP_RIGHT });
    }
    setSaving(false);
  };

  const handleSaveTemplate = async () => {
    if (!template.trim()) {
      toast.error('Template cannot be empty', { position: toast.POSITION.TOP_RIGHT });
      return;
    }
    setSavingTemplate(true);
    try {
      await setAISummaryTemplate(template);
      toast.success('Summary template saved successfully!', { position: toast.POSITION.TOP_RIGHT });
    } catch (e) {
      toast.error('Failed to save template. Please try again.', { position: toast.POSITION.TOP_RIGHT });
    }
    setSavingTemplate(false);
  };

  const handleResetTemplate = () => {
    setTemplate(DEFAULT_AI_SUMMARY_TEMPLATE);
    toast.info('Template reset to default. Click Save to apply.', { position: toast.POSITION.TOP_RIGHT });
  };

  return (
    <div>
      {loading && <LoaderSpinner />}
      <div className="container-fluid p-0 adminDashboardMain">
        <SideBarMenu />
        <div className="container-fluid p-0 contentBody">
          <Header />
          <div className="innerBodyBox bgInnerDiv">
            <div className="contentInnerBody">
              <div className="titleDiv">
                <h2>Settings</h2>
              </div>
              <div className="contentMainBox">

                {/* Tabs */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '2px solid #e3eaf5' }}>
                  <button
                    onClick={() => setActiveTab(0)}
                    style={{
                      padding: '10px 20px',
                      border: 'none',
                      background: 'none',
                      fontWeight: activeTab === 0 ? '700' : '400',
                      color: activeTab === 0 ? '#024C9C' : '#666',
                      borderBottom: activeTab === 0 ? '2px solid #024C9C' : '2px solid transparent',
                      marginBottom: '-2px',
                      cursor: 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    Notification Emails
                  </button>
                  <button
                    onClick={() => setActiveTab(1)}
                    style={{
                      padding: '10px 20px',
                      border: 'none',
                      background: 'none',
                      fontWeight: activeTab === 1 ? '700' : '400',
                      color: activeTab === 1 ? '#024C9C' : '#666',
                      borderBottom: activeTab === 1 ? '2px solid #024C9C' : '2px solid transparent',
                      marginBottom: '-2px',
                      cursor: 'pointer',
                      fontSize: '14px',
                    }}
                  >
                    Consultant Summary Template
                  </button>
                </div>

                {/* Tab 1 — Notification Emails */}
                {activeTab === 0 && (
                  <div className="whiteBg p-4" style={{ borderRadius: '14px' }}>
                    <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '6px' }}>
                      Appointment Notification Emails
                    </h3>
                    <p style={{ color: '#666', marginBottom: '20px', fontSize: '14px' }}>
                      These email addresses will receive a notification whenever a new appointment is scheduled.
                    </p>

                    <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                      <input
                        type="email"
                        placeholder="Enter email address"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddEmail()}
                        style={{
                          flex: 1,
                          padding: '10px 14px',
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          fontSize: '14px',
                          outline: 'none',
                        }}
                      />
                      <button
                        onClick={handleAddEmail}
                        className="signInBtn mb-0"
                        style={{ padding: '10px 24px', whiteSpace: 'nowrap' }}
                      >
                        + Add
                      </button>
                    </div>

                    {emails.length > 0 ? (
                      <ul style={{ listStyle: 'none', padding: 0, marginBottom: '24px' }}>
                        {emails.map((email, i) => (
                          <li
                            key={i}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '10px 14px',
                              background: '#f8f9fa',
                              borderRadius: '8px',
                              marginBottom: '8px',
                              fontSize: '14px',
                              border: '1px solid #eee',
                            }}
                          >
                            <span>{email}</span>
                            <button
                              onClick={() => handleRemoveEmail(email)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#dc3545',
                                cursor: 'pointer',
                                fontSize: '20px',
                                lineHeight: 1,
                                padding: '0 4px',
                              }}
                              title="Remove"
                            >
                              ×
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p style={{ color: '#999', fontStyle: 'italic', marginBottom: '24px' }}>
                        No notification emails added yet.
                      </p>
                    )}

                    <button onClick={handleSave} disabled={saving} className="signInBtn mb-0">
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                )}

                {/* Tab 2 — Clinical Summary Template */}
                {activeTab === 1 && (
                  <div className="whiteBg p-4" style={{ borderRadius: '14px' }}>
                    <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '6px' }}>
                      Consultant Summary Template
                    </h3>
                    <p style={{ color: '#666', marginBottom: '20px', fontSize: '14px' }}>
                      Define the sections used when generating AI clinical summaries for consultants. Edit section headings and instructions as needed.
                    </p>

                    {loadingTemplate ? (
                      <p style={{ color: '#999' }}>Loading template...</p>
                    ) : (
                      <>
                        <textarea
                          value={template}
                          onChange={(e) => setTemplate(e.target.value)}
                          rows={24}
                          style={{
                            width: '100%',
                            padding: '14px',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            fontSize: '13.5px',
                            fontFamily: 'monospace',
                            lineHeight: '1.6',
                            outline: 'none',
                            resize: 'vertical',
                            marginBottom: '16px',
                          }}
                        />
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                          <button
                            onClick={handleSaveTemplate}
                            disabled={savingTemplate}
                            style={{
                              padding: '8px 20px',
                              background: '#024C9C',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '8px',
                              fontSize: '14px',
                              fontWeight: '600',
                              cursor: savingTemplate ? 'not-allowed' : 'pointer',
                              opacity: savingTemplate ? 0.6 : 1,
                            }}
                          >
                            {savingTemplate ? 'Saving...' : 'Save Template'}
                          </button>
                          <button
                            onClick={handleResetTemplate}
                            style={{
                              padding: '8px 20px',
                              border: '1px solid #ccc',
                              borderRadius: '8px',
                              background: '#fff',
                              color: '#555',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: '500',
                            }}
                          >
                            Reset to Default
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminSettings;
